import { useCallback, useEffect, useRef, useState } from 'react';
import { useOptionalSoundNotificationsContext } from '../contexts/SoundNotificationsContext';

export const SIGNALS_KEY = 'ct:signals';
export const LAST_SEEN_KEY = 'ct:signals:lastSeen';
export const MAX_SIGNALS = 50;

const VALID_DIRECTIONS = new Set(['long', 'short']);

function getStorage() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
  } catch {}
  try {
    if (typeof globalThis !== 'undefined' && globalThis.localStorage) return globalThis.localStorage;
  } catch {}
  return null;
}

export function loadSignals() {
  const storage = getStorage();
  if (!storage) return [];
  try {
    const raw = storage.getItem(SIGNALS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((s) => s && typeof s === 'object' && typeof s.coin === 'string')
      .slice(0, MAX_SIGNALS);
  } catch {
    return [];
  }
}

export function saveSignals(signals) {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(SIGNALS_KEY, JSON.stringify(signals.slice(0, MAX_SIGNALS)));
  } catch {}
}

export function loadLastSeen() {
  const storage = getStorage();
  if (!storage) return 0;
  try {
    const raw = storage.getItem(LAST_SEEN_KEY);
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

export function saveLastSeen(ts) {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(LAST_SEEN_KEY, String(ts));
  } catch {}
}

/**
 * Validate and normalize a payload received from the WS `signal_open` event.
 * Returns null when the payload is malformed (missing coin/direction etc.).
 */
export function normalizeSignal(detail) {
  if (!detail || typeof detail !== 'object') return null;
  const coin = typeof detail.coin === 'string' ? detail.coin.toUpperCase() : null;
  if (!coin) return null;
  const direction = typeof detail.direction === 'string' ? detail.direction.toLowerCase() : null;
  if (!direction || !VALID_DIRECTIONS.has(direction)) return null;
  const priceNum = Number(detail.price);
  const price = Number.isFinite(priceNum) ? priceNum : null;
  const tsNum = Number(detail.timestamp);
  const timestamp = Number.isFinite(tsNum) ? tsNum : Date.now();
  const timeframe = typeof detail.timeframe === 'string' ? detail.timeframe : '15m';
  const volumeUsdNum = Number(detail.volumeUsd);
  const volumeUsd = Number.isFinite(volumeUsdNum) && volumeUsdNum > 0 ? volumeUsdNum : null;
  return { coin, direction, timeframe, price, volumeUsd, timestamp };
}

export function countUnread(signals, lastSeenTs) {
  if (!Array.isArray(signals)) return 0;
  const seen = Number(lastSeenTs) || 0;
  let n = 0;
  for (const s of signals) {
    if ((Number(s?.timestamp) || 0) > seen) n++;
  }
  return n;
}

export function useCrossCoinSignals() {
  const [signals, setSignals] = useState(() => loadSignals());
  const [lastSeenTs, setLastSeenTs] = useState(() => loadLastSeen());
  const soundCtx = useOptionalSoundNotificationsContext();
  const soundCtxRef = useRef(soundCtx);

  useEffect(() => {
    soundCtxRef.current = soundCtx;
  }, [soundCtx]);

  useEffect(() => {
    saveSignals(signals);
  }, [signals]);

  useEffect(() => {
    saveLastSeen(lastSeenTs);
  }, [lastSeenTs]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = (event) => {
      const next = normalizeSignal(event?.detail);
      if (!next) return;
      setSignals((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return [next, ...list].slice(0, MAX_SIGNALS);
      });
      const ctx = soundCtxRef.current;
      if (ctx && typeof ctx.playTieredSound === 'function') {
        try {
          ctx.playTieredSound(0, 'signal');
        } catch {
          // sound failures must not affect signal accumulation
        }
      }
    };
    window.addEventListener('signalOpen', handler);
    return () => {
      window.removeEventListener('signalOpen', handler);
    };
  }, []);

  const markAllRead = useCallback(() => {
    setLastSeenTs(Date.now());
  }, []);

  const clear = useCallback(() => {
    setSignals([]);
    setLastSeenTs(Date.now());
  }, []);

  const unreadCount = countUnread(signals, lastSeenTs);

  return {
    signals,
    unreadCount,
    lastSeenTs,
    markAllRead,
    clear,
  };
}

export default useCrossCoinSignals;
